"use client";

import { useState, useEffect, useMemo } from "react";
import usePiBrowser from "@/features/landing/hooks/usePiBrowser";
import { approvePiPayment, completePiPayment, cancelPiPayment } from "@/utils/api";
import PiPaymentModal from "./PiPaymentModal";
import FeedbackModal from "./FeedbackModal";
import { toast } from "react-toastify";
import { useAuth } from "@/context/AuthContext";
import RailButton from "@/components/ui/RailButton";


function safeStringify(x) {
  try {
    const seen = new WeakSet();
    return JSON.stringify(x, (k, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      return v;
    });
  } catch {
    try { return String(x); } catch { return "[Unserializable]"; }
  }
}



function log(msg, data) {
  try {
    if (typeof window === "undefined") return;
    const search = window.location.search || "";
    const enabled =
      window.localStorage?.getItem("piDebug") === "1" ||
      /(?:^|[?&])debug=1(?:&|$)/.test(search);
    if (!enabled) return;

    if (!window.debugLog) {
      const el = document.createElement("pre");
      el.id = "pi-debug-log";
      el.style =
        "position:fixed;bottom:0;left:0;width:100%;max-height:200px;overflow:auto;background:#111;color:#0f0;padding:10px;font-size:10px;z-index:9999;";
      document.body.appendChild(el);
      window.debugLog = (m, d) => {
        el.textContent += `[${new Date().toISOString()}] ${m} ${
          d !== undefined ? safeStringify(d)
 : ""
        }\n`;
      };
      window.addEventListener("keydown", (e) => {
        if (e.key === "`") el.style.display = el.style.display === "none" ? "" : "none";
      });
    }

    window.debugLog(msg, data);
  } catch {}
}

export default function BuyWithPiButton({ product, priceInPi, user }) {
  const isPiBrowser = usePiBrowser();
  const { setPiToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  // ✅ client-safe token state
  const [hasPi, setHasPi] = useState(false);

useEffect(() => {
  if (typeof window === "undefined") return;

  const sync = () => setHasPi(!!window.sessionStorage.getItem("piAccessToken"));
  sync();

  window.addEventListener("storage", sync);
  return () => window.removeEventListener("storage", sync);
}, []);


  const userId = useMemo(
    () => user?.id ?? user?._id ?? user?.uid ?? user?.userId ?? null,
    [user]
  );

  const isDisabled = priceInPi == null || Number.isNaN(Number(priceInPi)) || loading;

const buttonLabel = loading
  ? step === "approving"
    ? "🔐 Approving..."
    : step === "waiting_verification"
    ? "🔄 Verifying..."
    : step === "delivering_product"
    ? "📦 Delivering..."
    : "Processing..."
  : !isPiBrowser
  ? "Open in Pi Browser"
  : !hasPi
  ? "Login with Pi"
  : `Buy with Pi (${Number(priceInPi || 0).toFixed(4) || "..."})`;


  useEffect(() => {
    toast.dismiss();
    if (step === "done") toast.success("🎉 Payment complete!");
    if (step === "error") toast.error("❌ Something went wrong.");
  }, [step]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleBuy = async () => {
    if (typeof window === "undefined") return;

      if (!isPiBrowser) {
    toast.info("📱 Open this page in the Pi Browser to continue.");
    return;
  }
 const ready = window.__piReady ? await window.__piReady : !!window.Pi;
if (!ready || !window.Pi) {
  toast.error("📱 Please open this page in the Pi Browser to proceed.");
  return;
}


    if (priceInPi == null || Number.isNaN(Number(priceInPi))) {
      toast.error("⚠️ Invalid price. Cannot proceed.");
      return;
    }

    try {
      setLoading(true);
      setStep("starting");

     // already initialized in PiBootstrap


      const existingToken = window.sessionStorage.getItem("piAccessToken");

     const auth = existingToken
  ? { accessToken: existingToken }
  : await window.Pi.authenticate(["username", "payments"], async (incompletePayment) => {
      try {
        // IMPORTANT: payment id is usually incompletePayment.paymentId, not identifier
        const pid = incompletePayment?.paymentId || incompletePayment?.identifier;
        if (pid) await cancelPiPayment({ paymentId: pid });
        toast.info("🧹 Cleared stuck payment.");
      } catch (err) {
        toast.error("❌ Couldn’t cancel previous payment.");
        // don't crash app
      }
    });


      if (!auth?.accessToken) throw new Error("No access token received from Pi.");

      setPiToken(auth.accessToken);
      window.sessionStorage.setItem("piAccessToken", auth.accessToken);
      setHasPi(true);

      const recipientFields =
        Array.isArray(product?.recipientFields) && product.recipientFields.length
          ? product.recipientFields
          : Object.entries(product?.recipient || {}).map(([k, v]) => ({
              field: `recipient.${k}`,
              value: v,
            }));

      const billingAccountId =
        product?.billingAccountId ||
        product?.billing?.accountId ||
        recipientFields.find((f) => f.field === "billing.accountId")?.value;

      const mergedRecipientFields = [
        ...recipientFields.filter((f) => f.field !== "billing.accountId"),
        ...(billingAccountId
          ? [{ field: "billing.accountId", value: String(billingAccountId).trim() }]
          : []),
      ];

      const metadata = {
        productId: product?.sku,
        productSku: product?.productSku || product?.sku || "unknown-product",
        offerId: product?.offerId || "default-offer",
        userId: userId || "unknown",
        billingAccountId: billingAccountId ? String(billingAccountId).trim() : undefined,
        "fields.billing.accountId": billingAccountId ? String(billingAccountId).trim() : undefined,
        recipient: product?.recipient || {},
        recipientFields: mergedRecipientFields,
        requiredFields: product?.requiredFields || [],
        recipientFieldsJson: safeStringify
(mergedRecipientFields || []),
        requiredFieldsCsv: (product?.requiredFields || []).join(","),
        country: product?.country,
        priceType: product?.priceType || (product?.hasRange ? "RANGE" : "FIXED"),
        productType: product?.productType || product?.type || "voucher",
        brandName: product?.brandName,
        sender: product?.senderDetails,
        delivery: product?.delivery,
      };

      await window.Pi.createPayment(
        {
          amount: Number(priceInPi),
          memo: `Purchase: ${product?.name || product?.productSku || "Unnamed Product"}`,
          metadata,
        },
        {
          onReadyForServerApproval: async (paymentId) => {
            log("pi.approve", { paymentId });
            try {
              setStep("approving");

              const recipientFieldsMeta = Array.isArray(product?.recipientFields)
                ? product.recipientFields
                : [];

              const billingAccountIdMeta =
                product?.billingAccountId ||
                product?.billing?.accountId ||
                recipientFieldsMeta.find((f) => f.field === "billing.accountId")?.value;

              const safeProduct = {
                ...product,
                productSku: product?.productSku || product?.sku || "unknown-product",
                offerId: product?.offerId || product?.sku || "default-offer",
                memo: `Purchase: ${product?.name || product?.productSku || "Unnamed Product"}`,
                recipient: product?.recipient || {},
                recipientDetails: product?.recipientDetails || product?.recipient || {},
                recipientFields: [
                  ...recipientFieldsMeta.filter((f) => f.field !== "billing.accountId"),
                  ...(billingAccountIdMeta
                    ? [{ field: "billing.accountId", value: String(billingAccountIdMeta).trim() }]
                    : []),
                ],
                requiredFields: product?.requiredFields || [],
                billingAccountId: billingAccountIdMeta,
                billing: billingAccountIdMeta
                  ? { accountId: String(billingAccountIdMeta).trim() }
                  : undefined,
                userId: userId || undefined,
                country: product?.country || product?.fullDetail?.country,
              };

              if (!product?.isFixedPrice) {
                const asNum = (v) => {
                  const n = Number(v);
                  return Number.isFinite(n) ? n : null;
                };
                const pi = asNum(priceInPi);
                if (pi != null && pi > 0) {
                  safeProduct.priceInPi = pi;
                  safeProduct.piAmount = pi;
                }
                const usd = asNum(product?.amountUSD) ?? asNum(product?.priceUSD) ?? null;
                if (usd != null && usd > 0) safeProduct.amountUSD = usd;
              }

              delete safeProduct.accessToken;

             try {
  await approvePiPayment({ paymentId, product: safeProduct });
  toast.success("🟢 Payment approved");
} catch (err) {
  const message = err?.response?.data?.error || err?.message || safeStringify
(err);

  // allow idempotent "already approved" messages to pass
  if (
    String(message).includes("already handled") ||
    String(message).includes("already approved")
  ) {
    toast.info("🔁 Payment already in progress");
    return;
  }

  toast.error(`❌ Failed to approve payment: ${message}`);
  setStep("error");
  return; // ✅ IMPORTANT
}
            } catch (err) {
              setStep("error");
              const message = err?.response?.data?.error || err?.message || "Unknown approval error";
              log("approve.error", { message, status: err?.response?.status });
              toast.error(`❌ Failed to approve payment: ${message}`);
              throw err;
            }
          },

          onReadyForServerCompletion: async (paymentId, txid) => {
            log("pi.complete", { paymentId, txid });
            try {
              setStep("waiting_verification");
              await delay(1200);

              const res = await completePiPayment({ paymentId, txid });

              const deliveryStatus = res?.deliveryStatus || res?.delivery?.status;
              const zenditError =
                res?.deliveryError || res?.delivery?.error || res?.delivery?.raw?.message;

              setStep("delivering_product");

          const rawStatus =
  res?.deliveryStatus ??
  res?.delivery?.status ??
  res?.status ??
  "";

const status = String(rawStatus).toLowerCase().trim();

// If backend returned any redeem code / pin / code, treat as delivered
const hasCode =
  !!res?.redeemCode ||
  !!res?.code ||
  !!res?.pin ||
  !!res?.delivery?.redeemCode ||
  !!res?.delivery?.code ||
  !!res?.delivery?.pin;

const delivered =
  hasCode ||
  ["delivered", "success", "succeeded", "completed", "complete"].includes(status);

if (delivered) {
  toast.success("✅ Product delivered!");

  if (!showFeedback) {
    setTimeout(() => {
      toast.info("💬 Enjoying ReloadPi? Tap to leave feedback!", {
        position: "bottom-center",
        autoClose: false,
        closeOnClick: true,
        onClick: () => setShowFeedback(true),
      });
    }, 1500);
  }
} else {
  const zenditError =
    res?.deliveryError || res?.delivery?.error || res?.delivery?.raw?.message;

  toast.warn(
    `⚠️ Payment succeeded, but delivery status unclear: ${
      zenditError || rawStatus || "Unknown"
    }`
  );
}


              setStep("done");

              const event = new CustomEvent("reload-transactions");
              window.dispatchEvent(event);
            } catch (err) {
              setStep("error");
              const message = err?.response?.data?.error || err?.message || "Unknown completion error";
              log("complete.error", { message, status: err?.response?.status });
              toast.error(`❌ Payment failed: ${message}`);
            }
          },

          onCancel: async () => {
            setStep("error");
            toast.info("❌ Payment cancelled");
          },

         onError: (error, payment) => {
  const msg = error?.message || error?.error || safeStringify
(error);
  log("pi.onError", { msg, payment });
  setStep("error");
  toast.error(`❌ Pi SDK error: ${msg}`);
},
        }
      );
    } catch (err) {
      toast.error(`❌ ${err?.message || "Pi Payment failed"}`);
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  return (
  <>
    <RailButton
      theme="pi"
      onClick={handleBuy}
      disabled={isDisabled}
      loading={loading}
      loadingText={
        step === "approving"
          ? "Approving…"
          : step === "waiting_verification"
            ? "Verifying…"
            : step === "delivering_product"
              ? "Delivering…"
              : "Processing…"
      }
      hint={!isPiBrowser ? "Open in Pi Browser to pay" : !hasPi ? "Authenticate with Pi to continue" : "Secure Pi checkout"}
      badge="Pi Network"
      className="mt-3"
    >
      {buttonLabel}
    </RailButton>

    {step && (
      <div className="text-sm text-textMuted mt-2 text-center">
        {step === "approving" && "⏳ Approving payment..."}
        {step === "waiting_verification" && "🔗 Waiting for blockchain confirmation..."}
        {step === "delivering_product" && "📦 Delivering product..."}
        {step === "done" && "✅ Payment complete!"}
        {step === "error" && "❌ Something went wrong."}
      </div>
    )}

    {step && <PiPaymentModal step={step} onClose={() => setStep(null)} />}
    {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} user={user} />}
  </>
);
}
